/**
 * Row order of a nested-table level: a list of sort keys applied in turn (the
 * next key breaks ties), each a column - by field or header, case / spaces /
 * punctuation ignored - or a formula over columns, e.g. `[Volume '26] + [Volume '27]`.
 * Numbers compare as numbers, everything else as text (natural order: "2" < "10");
 * empty values always go last.
 */

import { evaluateExpression } from '../../design-builder/utils/design-formula.utils';
import type { NestedSortKey, NestedTableColumn, NestedTableRow } from './nested-table.types';

const looseKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Sort keys typed as text, one per comma: `floor_pct desc, oem_serial, [Vol '26] + [Vol '27] desc`.
 * A key ends in `asc` / `desc` (default asc); commas inside `[...]` or `(...)` belong to the key.
 */
export function parseNestedSort(text: string | null | undefined): NestedSortKey[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of String(text ?? '')) {
    if (char === '[' || char === '(') depth++;
    if (char === ']' || char === ')') depth = Math.max(0, depth - 1);
    if (char === ',' && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = /^(.*?)\s+(asc|desc)$/i.exec(part);
      const field = (match ? match[1] : part).trim();
      return { field, direction: (match?.[2].toLowerCase() === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc' };
    })
    .filter((key) => !!key.field);
}

/** One configured sort rule: one column, or several whose SUM is sorted on. */
export interface NestedSortRule {
  columns: string[];
  direction: 'asc' | 'desc';
}

/** A rule as the table's sort key: a column's name, or `[a] + [b]` for a sum. */
export function sortRuleToKey(rule: NestedSortRule): NestedSortKey | null {
  const columns = (rule.columns ?? []).map((column) => String(column).trim()).filter(Boolean);
  if (!columns.length) return null;
  return { field: columns.length === 1 ? columns[0] : columns.map((column) => `[${column}]`).join(' + '), direction: rule.direction === 'desc' ? 'desc' : 'asc' };
}

/** A saved sort key back as a rule: `[a] + [b]` → the summed columns, else the one column. */
export function sortKeyToRule(key: NestedSortKey): NestedSortRule {
  const field = String(key.field ?? '').trim();
  const sum = /^\[[^\]]+\](\s*\+\s*\[[^\]]+\])*$/.test(field) ? [...field.matchAll(/\[([^\]]+)\]/g)].map((match) => match[1].trim()) : null;
  return { columns: sum ?? (field ? [field] : []), direction: key.direction === 'desc' ? 'desc' : 'asc' };
}

/** The keys as typed text again (the builder's box). */
export function formatNestedSort(keys: NestedSortKey[] | null | undefined): string {
  return (keys ?? []).map((key) => `${key.field}${key.direction === 'desc' ? ' desc' : ''}`).join(', ');
}

/** A row's value for a column name: its key, else a column with that header's field (loosely matched). */
function rowValue(row: NestedTableRow, name: string, columns: readonly NestedTableColumn[]): unknown {
  if (name in row) return row[name];
  const wanted = looseKey(name);
  const key = Object.keys(row).find((candidate) => looseKey(candidate) === wanted);
  if (key !== undefined) return row[key];
  const column = columns.find((candidate) => looseKey(candidate.label ?? '') === wanted || looseKey(candidate.field ?? '') === wanted);
  return column?.field && column.field in row ? row[column.field] : undefined;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number(value.replace(/[,$%\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

/** A key's value for one row: a formula's result, else the column's value. */
function keyValue(row: NestedTableRow, key: NestedSortKey, columns: readonly NestedTableColumn[]): unknown {
  if (key.field.includes('[')) {
    const result = evaluateExpression(key.field, (name) => toNumber(rowValue(row, name, columns)));
    return result.status === 'ready' ? result.value : null;
  }
  return rowValue(row, key.field, columns);
}

const isEmpty = (value: unknown) => value === null || value === undefined || (typeof value === 'string' && !value.trim());

function compareValues(a: unknown, b: unknown): number {
  const left = toNumber(a);
  const right = toNumber(b);
  if (left !== null && right !== null) return left - right;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** A sorted copy of `rows` (the input is not changed); no keys = the same order. */
export function sortNestedRows(rows: readonly NestedTableRow[], keys: readonly NestedSortKey[], columns: readonly NestedTableColumn[] = []): NestedTableRow[] {
  if (!keys.length || rows.length < 2) return [...rows];
  const decorated = rows.map((row, index) => ({ row, index, values: keys.map((key) => keyValue(row, key, columns)) }));
  decorated.sort((x, y) => {
    for (let i = 0; i < keys.length; i++) {
      const a = x.values[i];
      const b = y.values[i];
      if (isEmpty(a) || isEmpty(b)) {
        if (isEmpty(a) && isEmpty(b)) continue;
        return isEmpty(a) ? 1 : -1;
      }
      const order = compareValues(a, b);
      if (order) return keys[i].direction === 'desc' ? -order : order;
    }
    return x.index - y.index;
  });
  return decorated.map((entry) => entry.row);
}
